/* Mímir — particle constellation engine (core glyph + satellite swarms)
 *
 * One large glyph (the Mímir rune ᛗ) holds the centre permanently, breathing
 * gently. Around it, loose nodes drift and orbit in soft swarms that ease
 * together into small glyphs (Korean, Chinese, Devanagari …), hold a moment,
 * then loosen and dissolve back into drift before reforming elsewhere.
 *
 * Satellite motion is a continuous per-particle steer toward a moving goal
 * (no hard morph), so gathering and dispersal read as organic, never an
 * "implosion". Clusters are placed in a clear zone away from the core glyph.
 *
 * Motion is frame-rate independent (delta-time based).
 *
 * Usage:
 *   MimirConstellation(canvasEl, {
 *     coreGlyph: 'ᛗ',
 *     satGlyphs: ['한','言','अ', ...],
 *     colors: { core:'#e6ba5a', dim:'#b88a4a', link:'rgba(230,186,90,0.16)' },
 *     count: 640,
 *     coreCenter: { x: 0.64, y: 0.5 },
 *     coreScale: 0.74,
 *     coreFraction: 0.55,
 *     satGlyphScale: 0.16,
 *     satClusters: 3,
 *     mouseRadius: 120,
 *     mousePush: 15
 *   });
 */

import { cssVar } from '@/utils/cssVar'

export interface ConstellationColors {
  core?: string
  dim?: string
  link?: string
}

export interface ConstellationCenter {
  x: number
  y: number
}

export interface ConstellationOptions {
  coreGlyph?: string
  satGlyphs?: string[]
  colors?: ConstellationColors
  count?: number
  coreCenter?: ConstellationCenter
  coreScale?: number
  coreFraction?: number
  satGlyphScale?: number
  satClusters?: number
  glyphFont?: string
  linkDist?: number
  mouseRadius?: number
  mousePush?: number
  ambientCount?: number
}

export type ParticleRole = 'core' | 'sat' | 'ambient'

export interface Particle {
  x: number
  y: number
  bx: number
  by: number
  vx: number
  vy: number
  dispX: number
  dispY: number
  tx: number
  ty: number
  role: ParticleRole
  cl: Cluster | null
  spare: boolean
  orbA: number
  orbS: number
  orbR: number
  seek: number
  formDelay: number
  spawnDelay: number
  breathePh: number
  breatheAmp: number
  size: number
  tw: number
  twS: number
}

export type ClusterState = 'swarm' | 'forming' | 'holding' | 'dispersing'

export interface Cluster {
  idx: number
  members: Particle[]
  state: ClusterState
  phaseStart: number
  glyph: string | null
  cx: number
  cy: number
  formAmt: number
  disp: number
}

export interface ConstellationHandle {
  destroy: () => void
  getParticles: () => Particle[]
  getCanvas: () => HTMLCanvasElement
}

type RGB = [number, number, number]

// Offscreen canvas clear colour — white is intentional for compositing glyph
// silhouettes via getImageData alpha. This is not a design colour; do not replace
// with a theme token.
const CANVAS_CLEAR_COLOR = '#ffffff'

function rand(a: number, b: number): number { return a + Math.random() * (b - a) }
function clamp(v: number, a: number, b: number): number { return v < a ? a : v > b ? b : v }
function easeInOut(t: number): number { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2 }
function smooth(rate: number, dt: number): number { return 1 - Math.pow(1 - rate, dt / 16.6667) }
function hexRgb(h: string): RGB {
  h = (h || '#ffffff').replace('#', '')
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
  const n = parseInt(h, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

export function MimirConstellation(canvas: HTMLCanvasElement, opts?: ConstellationOptions): ConstellationHandle {
  opts = opts || {}
  const coreGlyph = opts.coreGlyph || 'ᛗ'
  const satGlyphs = opts.satGlyphs || ['한', '言', 'अ']
  const colors = opts.colors || {}
  // Read theme tokens at engine-init time (cssVar returns fallback during SSR).
  // Fallbacks mirror the dark theme defaults (--art-rune-* in application.css).
  const coreColor = colors.core || cssVar('--art-rune-core', '#e6ba5a')
  const dimColor  = colors.dim  || cssVar('--art-rune-dim',  '#b88a4a')
  const linkColor = colors.link || cssVar('--art-rune-link', 'rgba(230,186,90,0.16)')
  const coreRGB = hexRgb(coreColor)
  const dimRGB = hexRgb(dimColor)
  const count = opts.count || 640
  const coreCenter = opts.coreCenter || { x: 0.5, y: 0.5 }
  const coreScale = opts.coreScale || 0.74
  const coreFraction = opts.coreFraction != null ? opts.coreFraction : 0.55
  const satGlyphScale = opts.satGlyphScale || 0.16
  const satClusters = opts.satClusters || 3
  const glyphFontTmpl = opts.glyphFont || '700 {px}px "Noto Sans Runic", "Cardo", "Noto Sans", system-ui, serif'
  // linkDist scales with canvas so density stays consistent across screen sizes
  const linkDistFrac = opts.linkDist ? opts.linkDist / 600 : 0.032
  const mouseRadius = opts.mouseRadius || 140
  const mousePush = opts.mousePush || 30
  const ambientCount = opts.ambientCount || 0

  // satellite phase timing (ms)
  const SWARM = 2800, FORM = 2800, HOLD = 4200, DISPERSE = 2600
  // initial spawn-in: nodes appear scattered across the stage, fade up, then a
  // gentle gravitational pull gathers them into the ᛗ. The spring is overdamped
  // so the gather is calm — no flashy overshoot or implosion.
  const GATHER_STAGGER = 800 // ms spread of when each node begins falling in
  const GATHER_STIFF = 12, GATHER_DAMP = 7 // overdamped spring (no overshoot)
  const FADE_DUR = 450 // ms for a node to fade up (while still scattered)

  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
  const off = document.createElement('canvas')
  const offCtx = off.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D

  let W = 0, H = 0, dpr = 1
  let particles: Particle[] = []
  let clusters: Cluster[] = []
  let ambientParticles: Particle[] = []
  let nCore = 0
  let satIdx = 0
  let effScale = coreScale // core scale after fit-clamp (never overflows the canvas)
  let spawnStart = 0 // timestamp the fade-in began (reset on every (re)build)
  let buildRetries = 0 // retries while the glyph samples to no points (font/layout not ready)
  let rafBuild: number | null = null
  // core glyph bounding metrics (set on resize) — exclusion zone for clusters
  const cC = { x: 0, y: 0 }
  let coreHalfW = 0, coreHalfH = 0, satHalf = 0
  const reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)

  let mouseX = -9999, mouseY = -9999, mouseActive = false

  function resize(): void {
    const r = canvas.getBoundingClientRect()
    // Integer dimensions: glyphPoints() uses W as the getImageData row stride,
    // so a fractional width (fractional-DPR / OS-scaled monitors) would index
    // the pixel buffer at non-integer offsets → no glyph points → the ᛗ never
    // forms and the nodes stay evenly scattered.
    W = Math.max(1, Math.floor(r.width)); H = Math.max(1, Math.floor(r.height))
    dpr = Math.min(window.devicePixelRatio || 1, 2)
    canvas.width = Math.round(W * dpr)
    canvas.height = Math.round(H * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    off.width = W; off.height = H
    const mn = Math.min(W, H)
    // Fit-clamp the core glyph so it always fits the stage at any aspect ratio
    // (otherwise on extreme sizes the ᛗ is pushed off-canvas and vanishes).
    // The sampled ᛗ box is ≈0.64 wide × 0.84 tall of the font px (= mn·scale).
    const pad = mn * 0.04
    effScale = Math.max(0.1, Math.min(
      coreScale,
      (W - 2 * pad) / (mn * 0.64),
      (H - 2 * pad) / (mn * 0.84),
    ))
    coreHalfW = effScale * mn * 0.32
    coreHalfH = effScale * mn * 0.42
    satHalf = satGlyphScale * mn * 0.62
    // keep the whole glyph box on-canvas around the requested centre
    cC.x = clamp(W * coreCenter.x, coreHalfW + pad, W - coreHalfW - pad)
    cC.y = clamp(H * coreCenter.y, coreHalfH + pad, H - coreHalfH - pad)
    build()
  }

  function build(): void {
    if (rafBuild != null) { cancelAnimationFrame(rafBuild); rafBuild = null }
    // Sample the core glyph first. If it yields no points (font/layout not yet
    // ready, or a transient zero-size canvas) retry next frame so the ᛗ never
    // ends up as scattered, shapeless noise — the cause of it "disappearing".
    const corePts = glyphPoints(coreGlyph, cC.x, cC.y, effScale, 4)
    if (!corePts.length && buildRetries < 30) {
      buildRetries++
      rafBuild = requestAnimationFrame(build)
      return
    }
    buildRetries = 0

    particles = []
    nCore = Math.floor(count * coreFraction)
    for (let i = 0; i < count; i++) {
      const sx = rand(0, W), sy = rand(0, H)
      particles.push({
        x: sx, y: sy, bx: sx, by: sy, vx: 0, vy: 0,
        dispX: 0, dispY: 0,
        tx: rand(0, W), ty: rand(0, H),
        role: i < nCore ? 'core' : 'sat', cl: null, spare: false,
        orbA: rand(0, Math.PI * 2), orbS: rand(0.12, 0.32) * (Math.random() < 0.5 ? -1 : 1),
        orbR: rand(0.3, 1.25), seek: rand(0.55, 1.15), formDelay: rand(0, 1100),
        spawnDelay: rand(0, GATHER_STAGGER),
        breathePh: rand(0, Math.PI * 2), breatheAmp: rand(0.6, 1.6),
        size: rand(0.7, 1.9), tw: rand(0, Math.PI * 2), twS: rand(0.0011, 0.0028),
      })
    }
    // Core particles target the big central glyph but START scattered (set above)
    // — the frame loop springs them in. Under reduced motion, snap into place.
    for (let c = 0; c < nCore; c++) {
      const p = particles[c]
      if (corePts.length) { const pt = corePts[c % corePts.length]; p.tx = pt[0]; p.ty = pt[1] }
      if (reduced) { p.x = p.bx = p.tx; p.y = p.by = p.ty }
    }
    makeClusters()
    // evenly spread ambient nodes across the stage
    ambientParticles = []
    if (ambientCount > 0) {
      const gcols = Math.max(1, Math.ceil(Math.sqrt(ambientCount * W / H)))
      const grows = Math.max(1, Math.ceil(ambientCount / gcols))
      const cw = W / gcols, ch = H / grows
      for (let i = 0; i < ambientCount; i++) {
        const col = i % gcols, row = Math.floor(i / gcols)
        const sx = (col + 0.2 + Math.random() * 0.6) * cw
        const sy = (row + 0.2 + Math.random() * 0.6) * ch
        ambientParticles.push({
          x: sx, y: sy, bx: sx, by: sy, vx: rand(-0.15, 0.15), vy: rand(-0.15, 0.15),
          dispX: 0, dispY: 0, tx: 0, ty: 0,
          role: 'ambient', cl: null, spare: false,
          orbA: rand(0, Math.PI * 2), orbS: rand(-0.0006, 0.0006), orbR: rand(0.4, 0.8),
          seek: 0, formDelay: 0, spawnDelay: 0,
          breathePh: rand(0, Math.PI * 2), breatheAmp: 0,
          size: rand(0.9, 1.5), tw: rand(0, Math.PI * 2), twS: rand(0.0008, 0.0018),
        })
      }
    }
    spawnStart = performance.now()
  }

  function glyphPoints(ch: string, ccx: number, ccy: number, scl: number, step: number): Array<[number, number]> {
    offCtx.clearRect(0, 0, W, H)
    const px = Math.round(Math.min(W, H) * scl)
    offCtx.fillStyle = CANVAS_CLEAR_COLOR
    offCtx.textAlign = 'center'
    offCtx.textBaseline = 'middle'
    offCtx.font = glyphFontTmpl.replace('{px}', String(px))
    offCtx.fillText(ch, ccx, ccy)
    let data: Uint8ClampedArray
    try { data = offCtx.getImageData(0, 0, W, H).data }
    catch (e) { return [] }
    const pts: Array<[number, number]> = []
    step = step || 3
    for (let y = 0; y < H; y += step) {
      for (let x = 0; x < W; x += step) {
        if (data[(y * W + x) * 4 + 3] > 128) pts.push([x + rand(-1.2, 1.2), y + rand(-1.2, 1.2)])
      }
    }
    for (let i = pts.length - 1; i > 0; i--) {
      const j = (Math.random() * (i + 1)) | 0; const t = pts[i]; pts[i] = pts[j]; pts[j] = t
    }
    return pts
  }

  // pick a cluster centre in a clear band, never overlapping the core glyph box
  function placeCluster(idx: number): [number, number] {
    const pad = satHalf + 18
    const margin = satHalf + 12
    // vertical bands: stack clusters up-right / right / down-right
    const ac = -0.62 + (idx % satClusters) * 0.62
    const ang = ac + rand(-0.16, 0.16)
    const baseR = Math.hypot(coreHalfW, coreHalfH) + satHalf + 14
    const radv = baseR * rand(1.0, 1.22)
    let cx = cC.x + Math.cos(ang) * radv
    let cy = cC.y + Math.sin(ang) * radv
    // hard exclusion: if inside the core box, shove out to its right edge
    if (Math.abs(cx - cC.x) < coreHalfW + pad && Math.abs(cy - cC.y) < coreHalfH + pad) {
      cx = cC.x + coreHalfW + pad
    }
    cx = Math.max(margin, Math.min(W - margin, cx))
    cy = Math.max(margin, Math.min(H - margin, cy))
    return [cx, cy]
  }

  function makeClusters(): void {
    clusters = []
    const sat = particles.slice(nCore)
    const per = Math.floor(sat.length / satClusters) || 1
    for (let c = 0; c < satClusters; c++) {
      const members = sat.slice(c * per, c === satClusters - 1 ? sat.length : (c + 1) * per)
      const cl: Cluster = { idx: c, members: members, state: 'swarm', phaseStart: 0, glyph: null, cx: 0, cy: 0, formAmt: 0, disp: 0 }
      members.forEach(function (p) { p.cl = cl })
      clusters.push(cl)
      enterSwarm(cl)
      // stagger so clusters are out of phase
      cl.phaseStart = performance.now() - rand(0, SWARM + (reduced ? 0 : FORM))
      if (reduced) { cl.state = 'holding'; cl.formAmt = 1 }
    }
  }

  function enterSwarm(cl: Cluster): void {
    cl.glyph = satGlyphs[satIdx % satGlyphs.length]; satIdx++
    const ctr = placeCluster(cl.idx); cl.cx = ctr[0]; cl.cy = ctr[1]
    const pts = glyphPoints(cl.glyph, cl.cx, cl.cy, satGlyphScale, 3)
    cl.members.forEach(function (p, k) {
      if (pts.length) { const pt = pts[k % pts.length]; p.tx = pt[0]; p.ty = pt[1]; p.spare = false }
      else { p.spare = true }
      p.formDelay = rand(0, 1100)
      if (reduced) { p.x = p.bx = p.tx; p.y = p.by = p.ty }
    })
    cl.state = 'swarm'; cl.phaseStart = performance.now(); cl.formAmt = 0; cl.disp = 0
  }

  function advanceCluster(cl: Cluster, now: number): void {
    if (reduced) { cl.formAmt = 1; cl.disp = 0; return }
    const e = now - cl.phaseStart
    if (cl.state === 'swarm') {
      cl.formAmt = 0
      if (e >= SWARM) { cl.state = 'forming'; cl.phaseStart = now }
    } else if (cl.state === 'forming') {
      cl.formAmt = easeInOut(Math.min(1, e / FORM))
      if (e >= FORM) { cl.state = 'holding'; cl.phaseStart = now }
    } else if (cl.state === 'holding') {
      cl.formAmt = 1
      if (e >= HOLD) { cl.state = 'dispersing'; cl.phaseStart = now; cl.disp = 0 }
    } else {
      cl.disp = easeInOut(Math.min(1, e / DISPERSE))
      cl.formAmt = 1 - cl.disp
      if (e >= DISPERSE) enterSwarm(cl)
    }
  }

  // Node count is fixed across screens; instead the link reach grows with width
  // so the M reads fuller on wide stages and less compact on narrow ones.
  // ~0.72× at phone width, 1.0× around 1024px, ~1.4× on wide desktops.
  function densityScale(): number { return clamp(W / 1024, 0.72, 1.4) }

  // spatial grid for constellation links (only between CORE particles — the
  // small satellite letters should read as clean dots, no link clutter)
  let grid: Record<string, Particle[]> = {}
  function linkKey(cx: number, cy: number): string { return cx + ',' + cy }
  function drawLinks(): void {
    grid = {}
    const cell = Math.min(W, H) * linkDistFrac * densityScale()
    for (let i = 0; i < nCore; i++) {
      const p = particles[i]
      const gx = (p.x / cell) | 0, gy = (p.y / cell) | 0
      const k = linkKey(gx, gy);
      (grid[k] || (grid[k] = [])).push(p)
    }
    ctx.lineWidth = 0.6
    ctx.strokeStyle = linkColor
    ctx.beginPath()
    for (const key in grid) {
      const parts = key.split(','); const gx2 = +parts[0], gy2 = +parts[1]
      const bucket = grid[key]
      for (let dx = 0; dx <= 1; dx++) {
        for (let dy = (dx === 0 ? 0 : -1); dy <= 1; dy++) {
          const nb = grid[linkKey(gx2 + dx, gy2 + dy)]
          if (!nb) continue
          for (let a = 0; a < bucket.length; a++) {
            const pa = bucket[a]
            const bStart = (nb === bucket) ? a + 1 : 0
            for (let b = bStart; b < nb.length; b++) {
              const pb = nb[b]
              const ddx = pa.x - pb.x, ddy = pa.y - pb.y
              const d2 = ddx * ddx + ddy * ddy
              if (d2 < cell * cell) { ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y) }
            }
          }
        }
      }
    }
    ctx.stroke()
  }

  function drawAmbientLinks(): void {
    if (!ambientParticles.length) return
    const maxDist = Math.min(W, H) * 0.13 * densityScale()
    const maxDist2 = maxDist * maxDist
    const n = ambientParticles.length
    ctx.lineWidth = 0.5
    ctx.strokeStyle = linkColor
    // ambient ↔ ambient
    for (let a = 0; a < n - 1; a++) {
      const pa = ambientParticles[a]
      for (let b = a + 1; b < n; b++) {
        const pb = ambientParticles[b]
        const ddx = pa.x - pb.x, ddy = pa.y - pb.y
        const d2 = ddx * ddx + ddy * ddy
        if (d2 < maxDist2) {
          const t = 1 - Math.sqrt(d2) / maxDist
          ctx.globalAlpha = t * t * 0.45
          ctx.beginPath()
          ctx.moveTo(pa.x, pa.y)
          ctx.lineTo(pb.x, pb.y)
          ctx.stroke()
        }
      }
    }
    // ambient ↔ core (the M): link each ambient node to its single nearest core
    // node in range. One clean line — the dense M would otherwise fan out into a
    // haze of hundreds of links from a single nearby ambient node.
    for (let a = 0; a < n; a++) {
      const pa = ambientParticles[a]
      let best2 = maxDist2, bx = 0, by = 0, found = false
      for (let c = 0; c < nCore; c++) {
        const pc = particles[c]
        const ddx = pa.x - pc.x, ddy = pa.y - pc.y
        const d2 = ddx * ddx + ddy * ddy
        if (d2 < best2) { best2 = d2; bx = pc.x; by = pc.y; found = true }
      }
      if (found) {
        const t = 1 - Math.sqrt(best2) / maxDist
        ctx.globalAlpha = t * t * 0.5
        ctx.beginPath()
        ctx.moveTo(pa.x, pa.y)
        ctx.lineTo(bx, by)
        ctx.stroke()
      }
    }
    ctx.globalAlpha = 1
  }

  let lastNow = performance.now()
  function frame(now: number): void {
    const dt = Math.min(50, now - lastNow); lastNow = now
    ctx.clearRect(0, 0, W, H)

    for (let ci = 0; ci < clusters.length; ci++) advanceCluster(clusters[ci], now)

    const dispEase = smooth(0.09, dt)
    const driftStep = dt / 16.6667
    const R2 = mouseRadius * mouseRadius
    const bt = now * 0.0006

    // very slow whole-M drift + tiny rocking rotation — the constellation breathes as one
    const slowT = now * 0.00009
    const driftScale = Math.min(W, H) * 0.012 // ~8px on a 700px stage
    let gDriftX = (Math.sin(slowT) * 0.7 + Math.cos(slowT * 0.62) * 0.45) * driftScale
    let gDriftY = (Math.cos(slowT * 0.77) * 0.55 + Math.sin(slowT * 0.43) * 0.35) * driftScale
    const gAng = Math.sin(slowT * 1.1) * 0.014 // ~±0.8°
    const gCos = reduced ? 1 : Math.cos(gAng), gSin = reduced ? 0 : Math.sin(gAng)
    if (reduced) { gDriftX = 0; gDriftY = 0 }

    for (let i = 0; i < particles.length; i++) {
      const p = particles[i]

      // 1) base position
      if (p.role === 'core') {
        if (reduced) { p.bx = p.tx; p.by = p.ty }
        else {
          // the glyph position this node belongs to (with the gentle whole-M breathe/drift)
          const rdx = p.tx - cC.x, rdy = p.ty - cC.y
          const rx = rdx * gCos - rdy * gSin
          const ry = rdx * gSin + rdy * gCos
          const goalX = cC.x + gDriftX + rx + Math.cos(bt + p.breathePh) * p.breatheAmp * 0.9
          const goalY = cC.y + gDriftY + ry + Math.sin(bt * 1.25 + p.breathePh) * p.breatheAmp * 0.9
          if (now - spawnStart >= p.spawnDelay) {
            // gravitational gather: an overdamped spring eases the node in from
            // its scattered start, then holds it on the glyph (tracking the breathe).
            const dts = dt / 1000
            const ax = (goalX - p.bx) * GATHER_STIFF - p.vx * GATHER_DAMP
            const ay = (goalY - p.by) * GATHER_STIFF - p.vy * GATHER_DAMP
            p.vx += ax * dts; p.vy += ay * dts
            p.bx += p.vx * dts; p.by += p.vy * dts
          }
          // before its delay elapses the node holds at its scattered start (just twinkling)
        }
      } else if (reduced) {
        p.bx = p.tx; p.by = p.ty
      } else {
        const cl = p.cl as Cluster
        const e = now - cl.phaseStart
        // are we steering toward the crisp glyph, or loosely orbiting?
        const formed = !p.spare && (cl.state === 'holding' ||
          (cl.state === 'forming' && e >= p.formDelay))
        let goalX: number, goalY: number
        if (formed) {
          goalX = p.tx + Math.cos(bt + p.breathePh) * p.breatheAmp
          goalY = p.ty + Math.sin(bt * 1.25 + p.breathePh) * p.breatheAmp
        } else {
          // loose orbiting swarm around the cluster centre; loosens while dispersing
          p.orbA += p.orbS * driftStep * 0.05
          const expand = 1 + (cl.state === 'dispersing' ? cl.disp * 2.4 : 0)
          const rr = satHalf * p.orbR * expand * (1 + 0.18 * Math.sin(now * 0.0011 + p.breathePh))
          goalX = cl.cx + Math.cos(p.orbA) * rr
          goalY = cl.cy + Math.sin(p.orbA) * rr * 0.92
        }
        // continuous soft steer — gentle ease, never an implosion
        const rate = smooth(0.032 * p.seek, dt)
        p.bx += (goalX - p.bx) * rate
        p.by += (goalY - p.by) * rate
      }

      // 2) eased mouse repulsion
      let desiredX = 0, desiredY = 0
      if (mouseActive) {
        const mdx = p.bx - mouseX, mdy = p.by - mouseY
        const md2 = mdx * mdx + mdy * mdy
        if (md2 < R2 && md2 > 0.0001) {
          const md = Math.sqrt(md2)
          let f = 1 - md / mouseRadius; f = f * f
          desiredX = (mdx / md) * f * mousePush
          desiredY = (mdy / md) * f * mousePush
        }
      }
      p.dispX += (desiredX - p.dispX) * dispEase
      p.dispY += (desiredY - p.dispY) * dispEase

      p.x = p.bx + p.dispX
      p.y = p.by + p.dispY
    }

    // ambient wander — slow heading drift with edge repulsion
    const edgePad = Math.min(W, H) * 0.06
    for (let i = 0; i < ambientParticles.length; i++) {
      const p = ambientParticles[i]
      p.orbA += p.orbS * dt
      p.vx += Math.cos(p.orbA) * 0.0015 * dt * p.orbR
      p.vy += Math.sin(p.orbA) * 0.0015 * dt * p.orbR
      const spd = Math.sqrt(p.vx * p.vx + p.vy * p.vy)
      const maxSpd = 0.3 * p.orbR
      if (spd > maxSpd && spd > 0) { p.vx = p.vx / spd * maxSpd; p.vy = p.vy / spd * maxSpd }
      if (p.x < edgePad) p.vx += (edgePad - p.x) * 0.0004 * dt
      if (p.x > W - edgePad) p.vx -= (p.x - W + edgePad) * 0.0004 * dt
      if (p.y < edgePad) p.vy += (edgePad - p.y) * 0.0004 * dt
      if (p.y > H - edgePad) p.vy -= (p.y - H + edgePad) * 0.0004 * dt
      p.x = clamp(p.x + p.vx, 0, W)
      p.y = clamp(p.y + p.vy, 0, H)
    }

    // links strengthen as the nodes gather into the M (sparse while scattered)
    const linkAppear = reduced ? 1 : easeInOut(clamp((now - spawnStart) / (GATHER_STAGGER + FADE_DUR), 0, 1))
    ctx.globalAlpha = linkAppear
    drawLinks()
    ctx.globalAlpha = 1
    drawAmbientLinks()

    for (let j = 0; j < particles.length; j++) {
      const q = particles[j]
      q.tw += q.twS * dt
      const tw = reduced ? 1 : (0.58 + 0.42 * Math.sin(q.tw))
      // nodes fade up early (while still scattered), then get pulled into the M
      const appear = reduced ? 1 : easeInOut(clamp((now - spawnStart - q.spawnDelay * 0.25) / FADE_DUR, 0, 1))
      const lit = q.role === 'core' ? 1 : (q.spare ? 0 : (q.cl as Cluster).formAmt)
      const r = (coreRGB[0] * lit + dimRGB[0] * (1 - lit)) | 0
      const g = (coreRGB[1] * lit + dimRGB[1] * (1 - lit)) | 0
      const b = (coreRGB[2] * lit + dimRGB[2] * (1 - lit)) | 0
      ctx.fillStyle = 'rgb(' + r + ',' + g + ',' + b + ')'
      const s = q.size
      ctx.globalAlpha = (0.05 + 0.04 * lit) * tw * appear
      ctx.beginPath(); ctx.arc(q.x, q.y, s * 1.8, 0, Math.PI * 2); ctx.fill()
      ctx.globalAlpha = (0.55 + 0.40 * lit) * tw * appear
      ctx.beginPath(); ctx.arc(q.x, q.y, s, 0, Math.PI * 2); ctx.fill()
    }
    ctx.globalAlpha = 1

    for (let j = 0; j < ambientParticles.length; j++) {
      const q = ambientParticles[j]
      q.tw += q.twS * dt
      const tw = reduced ? 1 : (0.5 + 0.5 * Math.sin(q.tw))
      // soft outer glow
      ctx.fillStyle = 'rgb(' + coreRGB[0] + ',' + coreRGB[1] + ',' + coreRGB[2] + ')'
      ctx.globalAlpha = 0.08 * tw
      ctx.beginPath(); ctx.arc(q.x, q.y, q.size * 2.2, 0, Math.PI * 2); ctx.fill()
      // crisp core dot
      ctx.globalAlpha = 0.65 * tw
      ctx.beginPath(); ctx.arc(q.x, q.y, q.size, 0, Math.PI * 2); ctx.fill()
    }
    ctx.globalAlpha = 1

    raf = requestAnimationFrame(frame)
  }

  // ---- pointer tracking (canvas is pointer-events:none, listen on window) ----
  function onMove(e: PointerEvent | TouchEvent): void {
    const r = canvas.getBoundingClientRect()
    const touches = (e as TouchEvent).touches
    const cx = (touches ? touches[0].clientX : (e as PointerEvent).clientX)
    const cy = (touches ? touches[0].clientY : (e as PointerEvent).clientY)
    mouseX = cx - r.left; mouseY = cy - r.top
    mouseActive = mouseX >= -mouseRadius && mouseX <= W + mouseRadius &&
                  mouseY >= -mouseRadius && mouseY <= H + mouseRadius
  }
  function onLeave(): void { mouseActive = false }
  window.addEventListener('pointermove', onMove as EventListener, { passive: true })
  window.addEventListener('pointerdown', onMove as EventListener, { passive: true })
  window.addEventListener('blur', onLeave)
  document.addEventListener('mouseleave', onLeave)

  let raf: number | null = null
  const ro = new ResizeObserver(function () { resize() })
  ro.observe(canvas)
  resize()
  raf = requestAnimationFrame(frame)

  return {
    destroy: function (): void {
      if (raf != null) cancelAnimationFrame(raf)
      if (rafBuild != null) cancelAnimationFrame(rafBuild)
      ro.disconnect()
      window.removeEventListener('pointermove', onMove as EventListener)
      window.removeEventListener('pointerdown', onMove as EventListener)
      window.removeEventListener('blur', onLeave)
      document.removeEventListener('mouseleave', onLeave)
    },
    getParticles: function (): Particle[] { return particles },
    getCanvas: function (): HTMLCanvasElement { return canvas },
  }
}
